import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import HighlightIcon from '@mui/icons-material/HighlightOutlined';
import OpactityIcon from '@mui/icons-material/Opacity';
import PropTypes from 'prop-types';
import React from 'react';
import SlideIcon from '@mui/icons-material/Flip';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import {connect} from 'react-redux';
import {styled} from '@mui/material/styles';
import {useTranslation} from 'react-i18next';

import CompareDiff from './compare-diff-slider.jsx';
import CompareFade from './compare-fade-slider.jsx';
import {COMPARE_METHODS, UI_MODES} from '@explorer/constants';
import {setCompareMethod} from '@explorer/actions/compare';
import {setMode} from '@explorer/actions/ui';
import {trackEvent} from '@explorer/analytics';
import {withDarkTheme} from '@explorer/styles';

const StyledToolbar = styled(Toolbar)(({theme}) => ({
  minHeight: theme.spacing(6),
  alignItems: 'center',
  justifyContent: 'space-between',
  backgroundColor: theme.palette.grey[800],
  padding: theme.spacing(0.5, 1),
  color: theme.palette.common.white,
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(0.5, 1),
  },
  zIndex: theme.zIndex.appBar,
}));

const StyledToggleButton = props => (
  <ToggleButton
    sx={{
      color: 'common.white',
      borderColor: 'grey.600',
      '&.Mui-selected': {
        backgroundColor: 'primary.main',
        '&:hover': {
          backgroundColor: 'primary.dark',
        },
      },
    }}
    {...props}
  />
);

const CompareTools = ({compareMethod, ...props}) => {
  const {t} = useTranslation();

  const onChangeMethod = (event, value) => {
    const compareMethod = value || COMPARE_METHODS.SLIDER;
    trackEvent('Changed compare method', {
      method: compareMethod,
    });
    props.setCompareMethod(compareMethod);
  };

  return (
    <StyledToolbar variant="dense" data-qe="compare-tools">
      <Box display="flex">
        <ToggleButtonGroup
          exclusive
          size="small"
          value={compareMethod}
          onChange={onChangeMethod}
        >
          <StyledToggleButton
            value={COMPARE_METHODS.SLIDER}
            data-qe="compare-slider-button"
          >
            <Tooltip title={t('compareTools.slide', 'Slide to compare')}>
              <SlideIcon />
            </Tooltip>
          </StyledToggleButton>

          <StyledToggleButton
            value={COMPARE_METHODS.FADE}
            data-qe="compare-fade-button"
          >
            <Tooltip
              title={t('compareTools.adjustOpacity', 'Adjust layer opacity')}
            >
              <OpactityIcon />
            </Tooltip>
          </StyledToggleButton>

          <StyledToggleButton
            value={COMPARE_METHODS.DIFF}
            data-qe="compare-diff-button"
          >
            <Tooltip
              title={t(
                'compareTools.relativeLuminance',
                'Compare relative luminance'
              )}
            >
              <HighlightIcon />
            </Tooltip>
          </StyledToggleButton>
        </ToggleButtonGroup>

        <Divider orientation="vertical" style={{margin: '0 8px'}} />

        {compareMethod === COMPARE_METHODS.DIFF && (
          <CompareDiff
            onChange={props.onPixelDiffChange}
            range={props.pixelDiffRangeValue}
          />
        )}

        {compareMethod === COMPARE_METHODS.FADE && (
          <CompareFade value={props.fadeValue} onChange={props.onFadeChange} />
        )}
      </Box>
      <Box display="flex" alignItems="center">
        <Button
          variant="contained"
          color="primary"
          size="mini"
          onClick={() => props.setMode(UI_MODES.BROWSE)}
        >
          {t('compareTools.stopComparison')}
        </Button>
      </Box>
    </StyledToolbar>
  );
};

CompareTools.propTypes = {
  compareMethod: PropTypes.string.isRequired,
  fadeValue: PropTypes.number,
  onFadeChange: PropTypes.func,
  onPixelDiffChange: PropTypes.func,
  pixelDiffRangeValue: PropTypes.array,
  setCompareMethod: PropTypes.func,
  setMode: PropTypes.func,
};

const mapStateToProps = state => ({
  compareMethod: state.compare.method,
});

const mapDispatchProps = {
  setCompareMethod,
  setMode,
};

export default connect(
  mapStateToProps,
  mapDispatchProps
)(withDarkTheme(CompareTools));
